import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet
} from 'react-router-dom';

// ============================================================================
// IMPORTS
// ============================================================================
import { HelpCircle } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoaderProvider, useLoader } from './contexts/LoaderContext';
import TopLoader from './components/ui/Loader';

import api from './services/api';

// ============================================================================
// PAGES
// ============================================================================
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employeesmanagement from './components/Employeesmanagement';
import Pointagerh from './components/Pointagerh';
import Sidebar from './components/Sidebar';
import KioskLayout from './components/KioskLayout'; // 🆕 Layout mode kiosque
import ComptabilitePaieRH from './components/ComptabilitePaieRH';
import Rapportsrh from './components/Rapportsrh';
import Documentsrh from './components/Documentsrh';
import Parametresrh from './components/Parametresrh';
import NotFound from './components/NotFound';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TestFeatureFlags from './pages/TestFeatureFlags';
import { RightsProvider, useRights } from './contexts/RightsContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { isCihHost } from './config/apiBase';


// ============================================================================
// AUTH GUARD
// ============================================================================
import AuthGuard from './components/AuthGuard';

// ============================================================================
// ROUTE PROTÉGÉE
// ============================================================================
const PrivateRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fdfcf5' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
               style={{ borderColor: '#88b9c5', borderTopColor: 'transparent' }}></div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
  <LoaderProvider>
    <RightsProvider>
      <FeatureFlagsProvider>
        <Outlet />
      </FeatureFlagsProvider>
    </RightsProvider>
  </LoaderProvider>
);
};

// ============================================================================
// ROUTE SUPER-ADMIN PROTÉGÉE
// ============================================================================
const SuperAdminRoute = () => {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Vérification des permissions...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Outlet />;
};

// ============================================================================
// 🆕 LAYOUT INTELLIGENT - Détecte le mode et affiche le bon layout
// ============================================================================
const SmartLayout = () => {
  const { rights, loading, isKioskMode } = useRights();
  const { loaderVisible } = useLoader();
  const [mobileWarningDismissed, setMobileWarningDismissed] = useState(false);
  const isMobile = window.innerWidth < 640;


  // Retirer le fond CSS login dès qu'on entre dans l'app
  useEffect(() => {
    if (window.__removeLoginBg) window.__removeLoginBg();
  }, []);
 
  
  if (loading || !rights) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#fdfcf5' }}>
        <TopLoader visible={true} />
      </div>
    );
  }
 
  if (isKioskMode()) {
    sessionStorage.setItem('kioskMode', 'true');
    return <KioskLayout />;
  }
 
  sessionStorage.removeItem('kioskMode');

  if (isMobile && !mobileWarningDismissed) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fdfcf5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        textAlign: 'center',
        fontFamily: 'DM Sans, sans-serif'
      }}>
        <img src="/logo.png" alt="HoraPilot" style={{ width: 64, height: 64, marginBottom: 24, objectFit: 'contain' }} />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a', marginBottom: 12, lineHeight: 1.3 }}>
          Optimisé pour tablette<br />et ordinateur
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 8, maxWidth: 320 }}>
          <strong style={{ color: '#448696' }}>HoraPilot Web</strong> est conçu pour les tablettes et ordinateurs. Pour une expérience optimale, ouvrez-le depuis un grand écran.
        </p>
        <p style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6, maxWidth: 320, marginTop: 8 }}>
          Une application mobile HoraPilot est en cours de développement. DANICED GROUP vous informera dès sa disponibilité.
        </p>
        <div style={{
          width: 48, height: 4, borderRadius: 4, margin: '20px auto',
          background: 'linear-gradient(90deg, #448696, #fed774, #fdac84)'
        }} />
        <button
          onClick={() => setMobileWarningDismissed(true)}
          style={{
            marginTop: 8, padding: '14px 28px',
            backgroundColor: '#448696', color: '#fdfcf5',
            border: 'none', borderRadius: 12,
            fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            width: '100%', maxWidth: 320,
          }}
        >
          Continuer quand même
        </button>
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: '#fed774', padding: '12px 16px',
          textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#1a1a1a'
        }}>
          HoraPilot by DANICED GROUP
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">

      <TopLoader visible={loaderVisible} />
      <Sidebar />
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
};

// ============================================================================
// APP COMPONENT
// ============================================================================
function App() {
const cihHost = isCihHost();

return (
    <AuthProvider>
      
        <Router>

        
        <Routes>
          {/* Connexion */}
          <Route path="/login" element={<Login />} />

          {/* ROUTE SUPER-ADMIN */}
          <Route element={<SuperAdminRoute />}>
            <Route path={cihHost ? "/" : "/super-admin"} element={<SuperAdminDashboard />} />
            {cihHost && <Route path="/super-admin" element={<Navigate to="/" replace />} />}
          </Route>

          {/* 🔥 Routes protégées avec SmartLayout (détecte auto kiosque ou normal) */}
          {!cihHost && (
          <Route element={<PrivateRoute />}>
            <Route element={<SmartLayout />}>
              
              {/* Route racine avec redirection intelligente */}
              <Route path="/" element={<AuthGuard />} />
              
              {/* Routes modules */}
              <Route 
                path="/dashboard" 
                element={<AuthGuard module="dashboard"><Dashboard /></AuthGuard>} 
              />
              
              <Route 
                path="/employees" 
                element={<AuthGuard module="employees"><Employeesmanagement /></AuthGuard>} 
              />
              
              <Route 
                path="/attendance" 
                element={<AuthGuard module="attendance"><Pointagerh /></AuthGuard>} 
              />
              
              <Route 
                path="/payroll" 
                element={<AuthGuard module="payroll"><ComptabilitePaieRH /></AuthGuard>} 
              />
              
              <Route 
                path="/reports" 
                element={<AuthGuard module="reports"><Rapportsrh /></AuthGuard>} 
              />
              
              <Route 
                path="/documents" 
                element={<AuthGuard module="documents"><Documentsrh /></AuthGuard>} 
              />
              
              <Route 
                path="/settings" 
                element={<AuthGuard module="settings"><Parametresrh /></AuthGuard>} 
              />
              

                {/* 🆕 PAGE DE TEST FEATURE FLAGS - Sans AuthGuard module car c'est pour tester */}
              <Route 
                path="/test-features" 
                element={<TestFeatureFlags />} 
              />
          </Route>
          </Route>
          )}

          {/* 404 */}
          <Route path="*" element={cihHost ? <Navigate to="/" replace /> : <NotFound />} />
        </Routes>
      </Router>
      
    </AuthProvider>
  );
}

export default App;
